import styled from '@emotion/styled'
import { Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'

import { RightLinkArrowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RightLinkArrowIcon'
import { SalesOfficeAvatarPlaceholderIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/SalesOfficeAvatarPlaceholderIcon'
import { shadow } from '@f-fertighaus/applications/Next/config/styles'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

import { useSalesOfficeData } from '../../../HouseSections/sections/VendorDetailsSection/hooks/useSalesOfficeData'
import { useUserPostcode } from '../../../HouseSections/sections/VendorDetailsSection/hooks/useUserPostcode'

export const SalesAgentSection = () => {
  const postcode = useUserPostcode()
  const { vendor } = useHouseDetail()

  const handlePersonalOfferClick = () => {
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'personal-offer-request',
      indicates: ['funnel-entry'],
      location: 'sticky-summary',
      values: [
        { type: 'text', value: t('houseDetailPage.sections.price.mainCtaText') }
      ]
    })
  }

  return postcode ? (
    <SalesAgent
      postcode={postcode}
      vendorName={vendor.display_name}
      vendorNid={vendor.nid}
    />
  ) : (
    <SalesAgentContainer>
      <Stack direction='row' spacing={2}>
        <SalesOfficeAvatarPlaceholderIcon />
        <Stack justifyContent='space-evenly'>
          <Stack direction='column' justifyContent='flex-start'>
            <Typography variant='body2'>
              {t('houseDetailPage.vendor.find-your-contact-now')}
            </Typography>
            <Typography fontWeight='fontWeightBold' variant='body2'>
              {vendor.display_name}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <PersonalOffer
                className='js-request-button'
                data-request-type='house-detail-form'
                data-funnel-variant='house-wizard-item-offer'
                {...(vendor.nid && { 'data-vendor-nid': vendor.nid })}
                onClick={handlePersonalOfferClick}>
                {t('houseDetailPage.sections.price.mainCtaText')}
                <RightLinkArrowIcon />
              </PersonalOffer>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </SalesAgentContainer>
  )
}

const SalesAgentContainer = styled(Box)`
  margin: ${({ theme }) => theme.spacing(1, 0)};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: var(--border-radius-card-large);
  label: SalesAgentContainer;
  box-shadow: ${shadow.actionCard};
  & img {
    border-radius: 50%;
  }
`

const PersonalOffer = styled('button')`
  color: ${({ theme }) => theme.palette.text.primary};
  display: flex;
  align-items: center;
  text-decoration: none;
  font-family: ${({ theme }) => theme.font.base};
  font-size: 14px;
  background: none;
  border: none;
  padding: 0;
  &:hover {
    cursor: pointer;
  }
`

const SalesAgent = ({
  vendorNid,
  postcode
}: {
  vendorNid: string
  postcode: string
  vendorName: string
}) => {
  const { data: salesOffice, status } = useSalesOfficeData(vendorNid, postcode)
  const { vendor } = useHouseDetail()

  const salesAgent = salesOffice?.faces[0]

  const notResolved = status === 'loading' || status === 'idle'

  if (notResolved || (salesOffice && salesOffice.faces?.length > 1)) {
    return null
  }

  return (
    <SalesAgentContainer>
      <Stack direction='row' spacing={2}>
        {salesAgent?.avatar ? (
          <img
            src={salesAgent.avatar}
            alt='consultant avatar'
            width={72}
            height={72}
          />
        ) : (
          <SalesOfficeAvatarPlaceholderIcon />
        )}
        <Box>
          <Box>
            <Typography fontWeight='fontWeightBold' variant='body2'>
              {salesAgent && salesAgent.first_name && salesAgent.last_name
                ? `${salesAgent?.first_name} ${salesAgent?.last_name}`
                : t('houseDetailPage.vendor.find-your-contact-now')}
            </Typography>
            <Typography variant='body2'>
              {salesOffice?.city || vendor.display_name}
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <PersonalOffer
              className='js-request-button'
              data-request-type='house-detail-form'
              data-funnel-variant='house-wizard-item-offer'
              data-vendor-nid={vendorNid}
              onClick={() => {
                webtrack({
                  category: 'CTA-button',
                  action: 'click',
                  label: 'personal-offer-request',
                  indicates: ['funnel-entry'],
                  location: 'sticky-summary',
                  values: [
                    {
                      type: 'text',
                      value: t('houseDetailPage.sections.price.mainCtaText')
                    }
                  ]
                })
              }}>
              {t('houseDetailPage.sections.price.mainCtaText')}
              <RightLinkArrowIcon />
            </PersonalOffer>
          </Box>
        </Box>
      </Stack>
    </SalesAgentContainer>
  )
}
